import { useState, useEffect, useMemo } from "react";
import { CT } from "@/content";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldAlert, Plus, Users, Clock, ChevronRight, Play, BarChart3, Trash2, Eye, RotateCcw, GraduationCap, BookOpenCheck, Trophy } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel
} from "@/components/ui/alert-dialog";

export default function SecureExamsHub() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/secure-exams`, { headers });
      setExams(res.data);
    } catch (e) {
      toast.error("Failed to load exams");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/secure-exams/${deleteId}`, { headers });
      setExams(prev => prev.filter(e => e.exam_id !== deleteId));
      toast.success("Exam deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeleteId(null); }
  };

  const togglePractice = async (examId, enabled) => {
    try {
      await axios.post(`${API}/secure-exams/${examId}/practice-mode`, { enabled }, { headers });
      setExams(prev => prev.map(e => e.exam_id === examId ? { ...e, practice_mode: enabled } : e));
      toast.success(enabled ? "Practice Mode ON — students can retake freely without lockdown" : "Practice Mode OFF — back to secure exam rules");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update practice mode");
    }
  };

  const toggleSolutions = async (examId, enabled) => {
    try {
      await axios.post(`${API}/secure-exams/${examId}/release-solutions`, { enabled }, { headers });
      setExams(prev => prev.map(e => e.exam_id === examId ? { ...e, solutions_released: enabled } : e));
      toast.success(enabled ? "Solutions released — students can now review answers & explanations" : "Solutions hidden from students");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update solutions");
    }
  };

  const toggleRanks = async (examId, enabled) => {
    try {
      await axios.post(`${API}/secure-exams/${examId}/release-ranks`, { enabled }, { headers });
      setExams(prev => prev.map(e => e.exam_id === examId ? { ...e, ranks_released: enabled } : e));
      toast.success(enabled ? "Ranks released — students can now see their class rank on the result screen" : "Ranks hidden from students");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update ranks");
    }
  };

  const isTeacher = user?.role === "teacher" || user?.role === "admin";
  const drafts = exams.filter(e => e.status === "draft");
  const published = exams.filter(e => e.status === "published");

  return (
    <div className="min-h-screen bg-background" data-testid="secure-exams-hub">
      <div className="border-b border-border bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-7 h-7 text-amber-400" />
            <div>
              <h1 className="text-xl font-bold"><CT k="secure_exams_title" /></h1>
              <CT k="olympiad_tagline" block className="text-xs text-slate-400" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(isTeacher ? "/teacher" : "/student")} className="text-white hover:bg-slate-800">
              Back
            </Button>
            {isTeacher && (
              <Button onClick={() => navigate("/secure-exams/new")} className="bg-amber-500 hover:bg-amber-400 text-slate-950" data-testid="create-exam-btn">
                <Plus className="w-4 h-4 mr-1" />New Exam
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {loading ? (
          <div className="text-center py-12 text-sm text-slate-500">Loading...</div>
        ) : isTeacher ? (
          <Tabs defaultValue="published" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="published" data-testid="tab-published">Published ({published.length})</TabsTrigger>
              <TabsTrigger value="drafts" data-testid="tab-drafts">Drafts ({drafts.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="published">
              <ExamGrid exams={published} isTeacher navigate={navigate} onDelete={setDeleteId} onTogglePractice={togglePractice} onToggleSolutions={toggleSolutions} onToggleRanks={toggleRanks} />
            </TabsContent>
            <TabsContent value="drafts">
              <ExamGrid exams={drafts} isTeacher navigate={navigate} onDelete={setDeleteId} onTogglePractice={togglePractice} onToggleSolutions={toggleSolutions} onToggleRanks={toggleRanks} />
            </TabsContent>
          </Tabs>
        ) : (
          <ExamGrid exams={exams} isTeacher={false} navigate={navigate} onDelete={() => {}} />
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Exam?</AlertDialogTitle>
            <AlertDialogDescription>This deletes the exam, all questions, and all student attempts. Cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ExamGrid({ exams, isTeacher, navigate, onDelete, onTogglePractice, onToggleSolutions, onToggleRanks }) {
  if (exams.length === 0) {
    return <div className="text-center py-16 text-slate-500 text-sm">No exams yet.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {exams.map(e => (
        <Card key={e.exam_id} className="p-5 hover:shadow-md transition-shadow" data-testid={`exam-card-${e.exam_id}`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-base truncate">{e.title}</h3>
              {e.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{e.description}</p>}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {e.practice_mode && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold" data-testid={`practice-badge-${e.exam_id}`}>practice</span>
              )}
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${e.status === "published" ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-700"}`}>
                {e.status}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-600 mb-4">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{e.duration_minutes} min</span>
            <span>{e.question_count || 0} Qs</span>
            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{(e.class_ids || []).length} classes</span>
          </div>
          {isTeacher ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => navigate(`/secure-exams/${e.exam_id}/edit`)} data-testid={`edit-${e.exam_id}`}>Edit</Button>
              {e.status === "published" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/secure-exams/${e.exam_id}/monitor`)} data-testid={`monitor-${e.exam_id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" />Monitor
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/secure-exams/${e.exam_id}/results`)} data-testid={`results-${e.exam_id}`}>
                    <BarChart3 className="w-3.5 h-3.5 mr-1" />Results
                  </Button>
                  <Button
                    size="sm"
                    variant={e.practice_mode ? "default" : "outline"}
                    onClick={() => onTogglePractice(e.exam_id, !e.practice_mode)}
                    className={e.practice_mode ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "text-emerald-700 border-emerald-300 hover:bg-emerald-50"}
                    data-testid={`toggle-practice-${e.exam_id}`}
                  >
                    <GraduationCap className="w-3.5 h-3.5 mr-1" />Practice: {e.practice_mode ? "ON" : "OFF"}
                  </Button>
                  <Button
                    size="sm"
                    variant={e.solutions_released ? "default" : "outline"}
                    onClick={() => onToggleSolutions(e.exam_id, !e.solutions_released)}
                    className={e.solutions_released ? "bg-sky-600 hover:bg-sky-500 text-white" : "text-sky-700 border-sky-300 hover:bg-sky-50"}
                    data-testid={`toggle-solutions-${e.exam_id}`}
                  >
                    <BookOpenCheck className="w-3.5 h-3.5 mr-1" />Solutions: {e.solutions_released ? "ON" : "OFF"}
                  </Button>
                  <Button
                    size="sm"
                    variant={e.ranks_released ? "default" : "outline"}
                    onClick={() => onToggleRanks(e.exam_id, !e.ranks_released)}
                    className={e.ranks_released ? "bg-amber-600 hover:bg-amber-500 text-white" : "text-amber-700 border-amber-300 hover:bg-amber-50"}
                    data-testid={`toggle-ranks-${e.exam_id}`}
                  >
                    <Trophy className="w-3.5 h-3.5 mr-1" />Ranks: {e.ranks_released ? "ON" : "OFF"}
                  </Button>
                </>
              )}
              <Button size="sm" variant="ghost" onClick={() => onDelete(e.exam_id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto" data-testid={`delete-${e.exam_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              {e.my_attempt?.status && ["submitted","auto_submitted","kicked"].includes(e.my_attempt.status) ? (
                <>
                  <span className="text-xs text-green-700 font-semibold">✓ Attempted</span>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/secure-exam/${e.exam_id}/result`)} data-testid={`view-result-${e.exam_id}`}>View Result</Button>
                  {e.practice_mode && (
                    <Button size="sm" onClick={() => navigate(`/secure-exam/${e.exam_id}/take`)} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`retake-${e.exam_id}`}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />Retake
                    </Button>
                  )}
                </>
              ) : (
                <Button size="sm" onClick={() => navigate(`/secure-exam/${e.exam_id}/take`)} className={e.practice_mode ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-amber-500 hover:bg-amber-400 text-slate-950"} data-testid={`take-exam-${e.exam_id}`}>
                  <Play className="w-3.5 h-3.5 mr-1" />
                  {e.my_attempt?.status === "in_progress" ? "Resume" : e.practice_mode ? "Start Practice" : "Start Exam"}
                  <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
